import gql from 'graphql-tag'
import {useQuery} from 'react-apollo'
import {Client} from '../../graphql/types'
import {PAMM_OFFER_TREE_FIELDS} from '../PammOffersTree/pammOfferFragment'

/**
 * Fetches the agent's PAMM offers for the hierarchical tree view.
 *
 * `includeDownline: true` widens the backend scope from the agent's directly-owned offers
 * to their whole downline (every nested sub-agent offer below them), which is what the
 * tree needs in order to render the agent's full structure.
 */
const ACCOUNTS_WITH_PAMM_AGENT_OFFERS = gql`
  query AccountsWithPammAgentOffers {
    viewer {
      ... on Client {
        id
        accounts(hidden: false, rejected: false) {
          ... on BaseAccount {
            pammAgentOffers(includeDownline: true) {
              ...PammOfferTreeFields
            }
          }
        }
      }
    }
  }
  ${PAMM_OFFER_TREE_FIELDS}
`

const isNotNil = <T>(value: T): value is NonNullable<T> => {
  if (typeof value === 'undefined') return false
  if (value === null) return false
  return true
}

export const usePammAgentOffers = () => {
  // `errorPolicy: 'all'`: the server denies `investors` on any node the viewer does not own with an
  // authorization error (PT-5708), and under the default policy ('none') Apollo discards `data`
  // entirely whenever any GraphQL error is present — one denied node would blank the whole tree.
  // A downline node is always owned (the agent's login is in its chain), so this is a guard rather
  // than an expected path here, but the failure it prevents is total and silent.
  const {loading, data, error} = useQuery<{viewer: Client}>(ACCOUNTS_WITH_PAMM_AGENT_OFFERS, {errorPolicy: 'all'})

  const pammAgentOffers =
    (data?.viewer?.accounts ?? [])
      .map((account) => account?.pammAgentOffers)
      .flat()
      .filter(isNotNil) ?? []

  // A per-field denial on one node must not present as a failed page when the tree came through.
  return {error: pammAgentOffers.length === 0 ? error : undefined, loading, pammAgentOffers}
}
